import {
  DeadlineStatusOption,
  leadSortType,
  LeadsQueryRequest,
  LeadsQuerySearchTypes
} from '../models/leads/leads.model';

export interface LeadsQueryFilters {
  fromDate?: Date | string;
  toDate?: Date | string;
  sortType?: leadSortType;
  sortDirection?: 'ASC' | 'DESC';
  search?: string;
  categoryKey?: string;
  leadTypes?: { Id: number }[];
  contactTypes?: { Id: number }[];
  leadStatuses?: { Id: number }[];
  countries?: { IsoCode?: string }[];
  deadlineStatus?: DeadlineStatusOption[];
  premiumProjectCustomerTypes?: { Id: number; Text: string }[];
  commissionSearches?: string[];
  contactSearches?: string[];
  responsibleSearches?: string[];
  openLeads?: boolean;
  closedLeads?: boolean;
  onlyMyLeads?: boolean;
  unreadLeads?: boolean;
  pageNumber?: number;
}

export class LeadsQueryRequestHelper {
  static build(
    filters: LeadsQueryFilters,
    userId: number,
    pageSize?: number,
    includePaging = true
  ): LeadsQueryRequest {
    const request: LeadsQueryRequest = {
      Searches: this.getQuerySearches(filters),
      Filter: {
        OnlyMine: !!filters.onlyMyLeads,
        OnlyUnread: !!filters.unreadLeads,
        OnlyPremiumPartner: !!filters.premiumProjectCustomerTypes?.length,
        ShowOpen: !!filters.openLeads,
        ShowClosed: !!filters.closedLeads,
        FromDateCreated: this.formatDate(filters.fromDate),
        ToDateCreated: this.formatDate(filters.toDate),
        ...this.getDeadlineRange(filters.deadlineStatus),
        LeadTypeIds: filters.leadTypes?.map(type => type.Id),
        LeadStateIds: filters.leadStatuses?.map(status => status.Id),
        ContactTypeIds: filters.contactTypes?.map(type => type.Id),
        CountryCodes: filters.countries?.map(country => country.IsoCode).filter(Boolean),
      },
      Sorting: this.getApiSortFields(filters),
      UserId: userId,
    };

    if (includePaging) {
      request.PageSize = pageSize;
      request.PageNumber = filters.pageNumber ?? 0;
    }

    return request;
  }

  private static getQuerySearches(filters: LeadsQueryFilters): LeadsQueryRequest['Searches'] {
    const searches: LeadsQueryRequest['Searches'] = [];
    const searchText = filters.search?.trim();

    if (searchText) {
      searches.push({
        SearchText: searchText,
        SearchTypes: this.getQueryFilterSearchType(filters.categoryKey),
      });
    }

    this.addQuerySearches(searches, filters.commissionSearches, LeadsQuerySearchTypes.ADDRESS);
    this.addQuerySearches(searches, filters.contactSearches, LeadsQuerySearchTypes.CONTACT);
    this.addQuerySearches(searches, filters.responsibleSearches, LeadsQuerySearchTypes.EDITOR);

    return searches.length ? searches : undefined;
  }

  private static addQuerySearches(
    searches: LeadsQueryRequest['Searches'],
    searchTexts: string[] | undefined,
    searchType: LeadsQuerySearchTypes
  ): void {
    searchTexts
      ?.map(item => item?.trim())
      .filter(Boolean)
      .forEach(item => searches.push({
        SearchText: item,
        SearchTypes: searchType,
      }));
  }

  private static getQueryFilterSearchType(categoryKey?: string): LeadsQuerySearchTypes | undefined {
    switch (categoryKey) {
      case 'Number':
        return LeadsQuerySearchTypes.LEADNUMBER;
      case 'Documents':
        return LeadsQuerySearchTypes.DOCUMENT;
      case 'Contact':
        return LeadsQuerySearchTypes.CONTACT;
      case 'History':
        return LeadsQuerySearchTypes.HISTORY;
      case 'Keywords':
        return LeadsQuerySearchTypes.KEYWORDS;
      default:
        return undefined;
    }
  }

  private static getDeadlineRange(
    selectedStatus?: DeadlineStatusOption[]
  ): { HideGreenDeadline?: boolean; HideOrangeDeadline?: boolean, HideRedDeadline?: boolean } {
    if (!selectedStatus?.length) {
      return {
        HideGreenDeadline: false,
        HideOrangeDeadline: false,
        HideRedDeadline: false,
      };
    }

    const selectedIds = new Set(selectedStatus.map((status) => status.Id));

    return {
      HideGreenDeadline: !selectedIds.has('green'),
      HideOrangeDeadline: !selectedIds.has('orange'),
      HideRedDeadline: !selectedIds.has('red'),
    };
  }

  private static getApiSortFields(filters: LeadsQueryFilters): string[] {
    const baseSortFields = this.getSortFields(filters)
      .map((field: string) => field.includes('Deadline') && filters.closedLeads && !filters.openLeads
        ? field.replace('Deadline', 'DoneDate')
        : field);

    return [
      '-RequiresConfirmation',
      ...baseSortFields.filter((item) => item !== 'RequiresConfirmation' && item !== '-RequiresConfirmation')
    ];
  }

  private static getSortFields(filters: LeadsQueryFilters): string[] {
    const sortDirection = filters.sortDirection ?? 'ASC';

    switch (filters.sortType ?? 'DATE') {
      case 'LAST_ACTION':
        return this.setSortField('LastAction', sortDirection);
      case 'TYPE':
        return this.setSortField('LeadTypeId', sortDirection);
      case 'STATUS':
        return this.setSortField('StatusId', sortDirection);
      case 'CONTACT':
        return this.setSortField('Contact.FirstName', sortDirection, ['Contact.LastName']);
      case 'RESPONSIBLE':
        return this.setSortField('EditorName', sortDirection, ['ExternalDesc']);
      case 'DATE':
        return this.setSortField('Deadline', sortDirection);
      case 'LAND':
        return this.setSortField('ResolvedAddress.CountryCode', sortDirection);
      case 'COMMISSION':
        return this.setSortField('Commission', sortDirection, ['ResolvedAddress.Address', 'ResolvedAddress.ZipCode', 'ResolvedAddress.City']);
      case 'DATE2':
        return this.setSortField('Number', sortDirection);
      default:
        return ['-Number'];
    }
  }

  private static setSortField(type: string, sortDirection: 'ASC' | 'DESC', additionalFields: string[] = []): string[] {
    const prefix = sortDirection === 'DESC' ? '-' : '';

    return [
      `${prefix}${type}`,
      ...additionalFields.map(item => `${prefix}${item}`),
    ];
  }

  private static formatDate(value?: Date | string): string | undefined {
    if (!value) {
      return undefined;
    }

    const date = new Date(value);

    if (Number.isNaN(date.valueOf())) {
      return undefined;
    }

    return date.toLocaleDateString('en-CA');
  }
}
